import {
    CpuIcon,
    GearIcon,
    KanbanIcon,
    KeyIcon,
    PlugsIcon,
    ReceiptIcon,
    UserIcon,
    UsersIcon,
    BookIcon,
    ClipboardTextIcon,
} from '@phosphor-icons/react';
import GlobalAssistants from './components/Utility/Icons/GlobalAssistants';
import { ReactElement } from 'react';
import React from 'react';
import { StoreSettings } from './Types/StoreTypes';

interface UserSettings {
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isAvency: boolean;
    isComplianceAdmin: boolean;
    isMaintainer: boolean;
}

interface AdminLink {
    to: string;
    label: string;
    icon: ReactElement;
    navigationHeaderIcon: ReactElement;
}

const getAdminLinks = (userSettings: UserSettings, settings: StoreSettings, i18n: any, demo: boolean): AdminLink[] => {
    let menuConfig: { to: string; labelId: string; translationSource: string; icon: ReactElement }[] = [];

    if (userSettings.isAdmin) {
        menuConfig.push({
            to: '/admin/dashboard',
            labelId: 'title',
            translationSource: 'Dashboard',
            icon: <KanbanIcon />,
        });
        if (settings.prePaidActive) {
            menuConfig.push({
                to: '/admin/billing',
                labelId: 'title',
                translationSource: 'Billing',
                icon: <ReceiptIcon />,
            });
        }
        menuConfig.push({ to: '/admin/users', labelId: 'users.title', translationSource: 'Users', icon: <UserIcon /> });
        menuConfig.push({ to: '/admin/groups', labelId: 'title', translationSource: 'Groups', icon: <UsersIcon /> });
        if (settings.embeddingModel) {
            menuConfig.push({
                to: '/admin/knowledgebases',
                labelId: 'title',
                translationSource: 'Knowledgebases',
                icon: <BookIcon />,
            });
        }
        if (settings.apiKeyLicences > 0) {
            menuConfig.push({
                to: '/admin/apikeys',
                labelId: 'title',
                translationSource: 'ApiKeys',
                icon: <KeyIcon />,
            });
        }
        menuConfig.push({
            to: '/admin/assistants',
            labelId: 'title.globalAssistants',
            translationSource: 'Prompts',
            icon: <GlobalAssistants />,
        });
    }

    if (userSettings.isSuperAdmin || demo) {
        menuConfig.push({
            to: '/admin/assistantfunctions',
            labelId: 'title',
            translationSource: 'PromptFunctions',
            icon: <PlugsIcon />,
        });
        menuConfig.push({ to: '/admin/models', labelId: 'title', translationSource: 'Models', icon: <CpuIcon /> });
    }

    if (userSettings.isSuperAdmin || demo) {
        menuConfig.push({
            to: '/admin/credentials',
            labelId: 'title',
            translationSource: 'Credentials',
            icon: <KeyIcon />,
        });
    }

    if (userSettings.isMaintainer || demo) {
        menuConfig.push({
            to: '/admin/logs',
            labelId: 'logs.title',
            translationSource: 'LogFiles',
            icon: <ClipboardTextIcon />,
        });
    }

    if (userSettings.isSuperAdmin) {
        menuConfig.push({ to: '/admin/settings', labelId: 'title', translationSource: 'Settings', icon: <GearIcon /> });
    }

    return menuConfig.map(({ to, labelId, translationSource, icon }) => ({
        to,
        label: i18n.translate({ id: labelId, sourceName: translationSource }),
        icon: React.cloneElement(icon as React.ReactElement<any, any>, { className: 'text-[24px] xl:text-[28px]' }),
        navigationHeaderIcon: React.cloneElement(icon as React.ReactElement<any, any>, { className: 'text-[24px]' }),
    }));
};

export const getComplianceLinks = (i18n: any): AdminLink[] => {
    const complianceConfig = [
        {
            to: '/compliance/dashboard',
            labelId: 'compliance.dashboard',
            translationSource: 'LogFiles',
            icon: <KanbanIcon />,
        },
        {
            to: '/compliance/logs',
            labelId: 'logs.title',
            translationSource: 'LogFiles',
            icon: <ClipboardTextIcon />,
        },
    ];

    return complianceConfig.map(({ to, labelId, translationSource, icon }) => ({
        to,
        label: i18n.translate({ id: labelId, sourceName: translationSource }),
        icon: React.cloneElement(icon, { className: 'text-[24px] xl:text-[28px]' }),
        navigationHeaderIcon: React.cloneElement(icon, { className: 'text-[24px]' }),
    }));
};

export default getAdminLinks;
